// ============================================================
// Timezone-aware date utilities
// All date range calculations should go through here so that
// "today", "this week", etc. are relative to the restaurant's
// configured timezone rather than the user's device/browser.
// ============================================================

const DEFAULT_TZ = 'Asia/Kolkata';

/**
 * Returns 'YYYY-MM-DD' for the given Date in the given IANA timezone.
 * e.g. getLocalDateStr(new Date(), 'Asia/Kolkata') → '2026-03-23'
 */
export function getLocalDateStr(date, timezone = DEFAULT_TZ) {
  // en-CA locale formats as YYYY-MM-DD which is ideal
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(date);
}

/**
 * Returns a UTC Date representing midnight (00:00:00) on `dateStr` in `timezone`.
 * dateStr: 'YYYY-MM-DD'
 *
 * Works by figuring out what time UTC midnight falls at in the given timezone,
 * then offsetting accordingly. Handles both UTC+ and UTC- zones correctly.
 */
export function tzMidnightUTC(dateStr, timezone = DEFAULT_TZ) {
  const utcMidnight = new Date(`${dateStr}T00:00:00Z`);

  // What wall-clock time does UTC midnight correspond to in this timezone?
  const localTimeStr = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).format(utcMidnight);

  // Parse "HH:mm:ss" → milliseconds since midnight
  const [h, m, s] = localTimeStr.split(':').map(Number);
  const localTimeMs = (h * 3600 + m * 60 + s) * 1000;

  // If UTC midnight is early in the local day (< 12:00 local) → timezone is AHEAD of UTC
  //   e.g. IST: UTC midnight = 05:30 IST → midnight IST = UTC midnight − 05:30
  // If UTC midnight is late in the local day (≥ 12:00 local) → timezone is BEHIND UTC
  //   e.g. New York: UTC midnight = 19:00 local → midnight local = UTC midnight + (24h − 19h)
  if (localTimeMs < 12 * 3600 * 1000) {
    return new Date(utcMidnight.getTime() - localTimeMs);
  } else {
    return new Date(utcMidnight.getTime() + (24 * 3600 * 1000 - localTimeMs));
  }
}

/**
 * Add `days` calendar days to a 'YYYY-MM-DD' string and return a new 'YYYY-MM-DD'.
 */
function addDaysToStr(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00Z`); // noon UTC avoids DST edge cases
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Returns { start, end, prevStart, prevEnd, label } as UTC Date objects,
 * where day boundaries are computed in `timezone`.
 *
 * range: 'today' | '7d' | 'month'
 */
export function getDateRangeInTz(range, timezone = DEFAULT_TZ) {
  const now = new Date();
  const todayStr = getLocalDateStr(now, timezone);

  if (range === 'today') {
    const start    = tzMidnightUTC(todayStr, timezone);
    const end      = now;
    const prevStart = tzMidnightUTC(addDaysToStr(todayStr, -1), timezone);
    const prevEnd   = new Date(start.getTime() - 1);
    return { start, end, prevStart, prevEnd, label: 'Today' };
  }

  if (range === '7d') {
    const startStr  = addDaysToStr(todayStr, -6);
    const start     = tzMidnightUTC(startStr, timezone);
    const end       = now;
    const prevStart = tzMidnightUTC(addDaysToStr(startStr, -7), timezone);
    const prevEnd   = new Date(start.getTime() - 1);
    return { start, end, prevStart, prevEnd, label: 'Last 7 Days' };
  }

  // month
  const [year, month] = todayStr.split('-').map(Number);
  const firstOfMonthStr = `${year}-${String(month).padStart(2, '0')}-01`;
  const prevMonthStr    = month === 1
    ? `${year - 1}-12-01`
    : `${year}-${String(month - 1).padStart(2, '0')}-01`;
  const lastOfPrevMonthStr = addDaysToStr(firstOfMonthStr, -1);

  const start     = tzMidnightUTC(firstOfMonthStr, timezone);
  const end       = now;
  const prevStart = tzMidnightUTC(prevMonthStr, timezone);
  const prevEnd   = new Date(tzMidnightUTC(addDaysToStr(lastOfPrevMonthStr, 1), timezone).getTime() - 1);
  return { start, end, prevStart, prevEnd, label: 'This Month' };
}

/**
 * Given a YYYY-MM-DD input-date string and a timezone, returns the UTC Date
 * for the start of that day (midnight) and end of that day (23:59:59.999).
 *
 * Used for custom date-range pickers.
 */
export function inputDateToUtcRange(dateStr, timezone = DEFAULT_TZ) {
  const start = tzMidnightUTC(dateStr, timezone);
  const end   = new Date(tzMidnightUTC(addDaysToStr(dateStr, 1), timezone).getTime() - 1);
  return { start, end };
}

/**
 * Returns today's date as 'YYYY-MM-DD' in the given timezone.
 * Convenience wrapper for getLocalDateStr(new Date(), timezone).
 */
export function todayInTz(timezone = DEFAULT_TZ) {
  return getLocalDateStr(new Date(), timezone);
}

/**
 * Returns a 'YYYY-MM-DD' string for the first day of the current month
 * in the given timezone.
 */
export function firstOfMonthInTz(timezone = DEFAULT_TZ) {
  const today = todayInTz(timezone);
  const [year, month] = today.split('-');
  return `${year}-${month}-01`;
}

/**
 * Extracts the hour (0–23) from a UTC timestamp string as seen in `timezone`.
 * Used for hourly analytics charts.
 */
export function getHourInTz(isoTimestamp, timezone = DEFAULT_TZ) {
  const d = new Date(isoTimestamp);
  return parseInt(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hourCycle: 'h23',
      hour12: false,
    }).format(d),
    10
  );
}

/**
 * Extracts the day-of-week (0=Sun … 6=Sat) from a UTC timestamp as seen in `timezone`.
 */
export function getDayOfWeekInTz(isoTimestamp, timezone = DEFAULT_TZ) {
  const d = new Date(isoTimestamp);
  // Get the local date string, then compute day of week from that
  const localStr = getLocalDateStr(d, timezone); // 'YYYY-MM-DD'
  return new Date(`${localStr}T12:00:00Z`).getUTCDay();
}

/**
 * Returns 'YYYY-MM-DD' (in timezone) for a given UTC timestamp string.
 * Used to group orders by calendar date.
 */
export function getLocalDateOfTs(isoTimestamp, timezone = DEFAULT_TZ) {
  return getLocalDateStr(new Date(isoTimestamp), timezone);
}

/** Common IANA timezone list for the admin timezone selector. */
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata',     label: 'India (IST, UTC+5:30)' },
  { value: 'Asia/Dubai',       label: 'Dubai (GST, UTC+4)' },
  { value: 'Asia/Singapore',   label: 'Singapore (SGT, UTC+8)' },
  { value: 'Asia/Kuala_Lumpur',label: 'Malaysia (MYT, UTC+8)' },
  { value: 'Asia/Bangkok',     label: 'Bangkok (ICT, UTC+7)' },
  { value: 'Asia/Colombo',     label: 'Sri Lanka (SLST, UTC+5:30)' },
  { value: 'Asia/Dhaka',       label: 'Bangladesh (BST, UTC+6)' },
  { value: 'Asia/Karachi',     label: 'Pakistan (PKT, UTC+5)' },
  { value: 'Asia/Kathmandu',   label: 'Nepal (NPT, UTC+5:45)' },
  { value: 'Europe/London',    label: 'UK (GMT/BST, UTC+0/+1)' },
  { value: 'Europe/Paris',     label: 'Central Europe (CET, UTC+1)' },
  { value: 'America/New_York', label: 'US Eastern (EST/EDT, UTC-5/-4)' },
  { value: 'America/Chicago',  label: 'US Central (CST/CDT, UTC-6/-5)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (PST/PDT, UTC-8/-7)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT, UTC+10/+11)' },
  { value: 'UTC',              label: 'UTC' },
];
